import { checkResponse } from "src/components/js/statics1";
import personSettingsClass, { fetchPersonSetting } from "./personSettingsClass";
import { asiakas_fetchList } from "../asiakas/AsiakasFunctions";
import personClass from "./personClass";
import {
  cache_clear,
  cache_read,
  cache_write,
  person_cacheName,
} from "src/components/js/CacheFunctions";

/**
 *
 * @param {string} personEmail
 * @param {string} creatorEmail_obsolete
 * @param {string} personFirstName
 * @param {string} personLastName
 * @param {string} password
 * @returns {{success: boolean, message: string, personId: number}}
 * @description Creates new person with email and password. Returns personId or error message.
 * @example const result = await createNewPerson(email, creatorEmail, firstName, lastName, password);
 * @example if (result.success) { console.log(result.personId); } else { console.log(result.message); }
 *
 */
export async function person_create(
  personEmail,
  personFirstName = "",
  personLastName = "",
  password = "",
  ownerAsiakasId = null,
  personPhone = "",
  personMemo = ""
) {
  const requestOptions = {
    method: "POST",
    headers: {
      //"x-access-token": `${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      personEmail: personEmail,
      personFirstName: personFirstName,
      personLastName: personLastName,
      password: password,
      ownerAsiakasId: ownerAsiakasId,
      personPhone: personPhone,
      personMemo: personMemo,
    }),
  };
  const response = await fetch(
    import.meta.env.VITE_SERVER_URL + "/api/person/newPerson",
    requestOptions
  );
  const cResponse = await checkResponse(response, "personClass.js:createNewPerson");
  if (!cResponse.success)
    return { success: false, message: cResponse.errorMessage, personId: null };
  if (cResponse.data.error)
    return { success: false, message: cResponse.data.message, personId: null };
  const result = cResponse.data;
  const newPersonId = result.data.returnValue;
  return { success: true, message: "Uusi henkilö luotu", personId: newPersonId };
}
export async function person_fetchList_forAsiakas(asiakasId, token) {
  const requestOptions = {
    method: "GET",
    headers: {
      "x-access-token": `${token}`,
      "Content-Type": "application/json",
    },
  };
  const fullUrl = import.meta.env.VITE_SERVER_URL + "/api/person/getPersonsForAsiakas/" + asiakasId;
  const c = cache_read(person_cacheName, fullUrl);
  if (c) return c;
  const response = await fetch(fullUrl, requestOptions);
  const cResponse = await checkResponse(response, "personFunctions.js:person_fetchList_forAsiakas");
  if (!cResponse.success) return [];
  const result = cResponse.data;
  cache_write(person_cacheName, fullUrl, result, 360);
  return result;
}
export async function person_fetchList_forTyomaa(tyomaaId, token) {
  const requestOptions = {
    method: "GET",
    headers: {
      "x-access-token": `${token}`,
      "Content-Type": "application/json",
    },
  };
  const fullUrl = import.meta.env.VITE_SERVER_URL + "/api/person/getPersonsForTyomaa/" + tyomaaId;
  const c = cache_read(person_cacheName, fullUrl);
  if (c) return c;
  const response = await fetch(fullUrl, requestOptions);
  const cResponse = await checkResponse(response, "personFunctions.js:person_fetchList_forTyomaa");
  if (!cResponse.success) return [];
  const result = cResponse.data;
  cache_write(person_cacheName, fullUrl, result, 360);
  return result;
}

export async function person_search(
  ownerAsiakasId,
  searchString,
  token,
  signal = null,
  usingFullTextSearch = true
) {
  // if searchString is only numbers then do not use full text search
  if (searchString.match(/^[0-9]+$/) != null) {
    usingFullTextSearch = false;
  }
  const body = {
    ownerAsiakasId: ownerAsiakasId,
    searchString: searchString,
    usingFullTextSearch: usingFullTextSearch,
    signal: signal,
  };
  const requestOptions = {
    method: "POST",
    headers: {
      "x-access-token": `${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  };
  const response = await fetch(
    import.meta.env.VITE_SERVER_URL + "/api/person/search/",
    requestOptions
  );
  const cResponse = await checkResponse(response, "searchPerson");
  if (!cResponse.success) {
    return [];
  }
  const result = cResponse.data;
  return result;
}

// fetchAsiakasIds only needed for person that is logged in
export async function person_fetch_by_email(
  email,
  token,
  fetchAsiakasIds = false,
  ownerAsiakasId = null
) {
  try {
    let person = new personClass();
    person.personEmail = email;
    if (email == null || email == "") {
      return null;
    }

    const fullUrl = import.meta.env.VITE_SERVER_URL + "/api/person/getPersonByEmail/" + email;
    const c = cache_read(person_cacheName, fullUrl);
    if (c) return c;
    const fetchResponse = await fetch(fullUrl, {
      headers: {
        "x-access-token": `${token}`,
        "Content-Type": "application/json",
      },
    })
      .then((response) => {
        if (response.ok) {
          return response;
        } else {
          person.personFirstName = response.status;
          person.personLastName = response.statusText;
          person.errorTxt = response.statusText;
          person.errorNum = response.status;
        }
      })
      .catch((err) => {
        console.log("FETCH ERROR:", err);
      });
    if (!fetchResponse || !fetchResponse.ok) {
      console.log("fetchPersonByEmail: failed to fetch person by email");
      return null;
    }

    const personJson = await fetchResponse.json();
    if (personJson.length > 0) {
      updatePersonFromJson(person, personJson);
    } else {
      console.log("person not found, creating new person");
      const personResult = await person_create(email, token, ownerAsiakasId);
      if (personResult.success == false) {
        console.log("failed to create new person");
        return null;
      }
      person.personId = personResult.personId;
      console.log("new person id: " + person.personId);
      // return person
      // no settings or asiakasIds yet as it is a new person
      cache_write(person_cacheName, fullUrl, person, 5);
      return person;
    }

    await updatePersonSettings(person, token);

    if (fetchAsiakasIds) {
      person.asiakasList = await asiakas_fetchList(
        person.personId,
        null,
        null,
        token,
        ownerAsiakasId
      );
      person.asiakasListIsFetched = true;
    }

    return person;
  } catch (err) {
    console.log(err);
  }
}

export async function person_fetch_by_id(
  personId,
  token,
  fetchAsiakasIds = false, // ownerAsiakasId is required if this is true
  ownerAsiakasId = null // only needed when fetchAsiakasIds is true
) {
  if (personId === undefined) return null;
  if (personId === null) return null;
  if (!personId) return null;
  if (personId == 0) return null;
  if (isNaN(personId)) return null;
  //const person = new personClass(personId, token);
  const fullUrl = "/api/person/get/" + personId;
  const c = cache_read(person_cacheName, fullUrl + "/" + ownerAsiakasId);
  if (c) return c;
  const response = await fetch(import.meta.env.VITE_SERVER_URL + fullUrl, {
    method: "GET",
    headers: {
      "x-access-token": `${token}`,
      "Content-Type": "application/json",
    },
  });
  const cResponse = await checkResponse(response, "personFunctions.js:person_fetch_by_id");
  if (!cResponse.success) return false;
  const personJson = cResponse.data;
  if (personJson.length == 0) return null;

  let person = new personClass(personId, token);
  person = updatePersonFromJson(person, personJson);
  await updatePersonSettings(person, token);
  if (fetchAsiakasIds) {
    person.asiakasList = await asiakas_fetchList(
      person.personId,
      null,
      null,
      token,
      ownerAsiakasId
    );
    person.asiakasListIsFetched = true;
  }
  cache_write(person_cacheName, fullUrl + "/" + ownerAsiakasId, person, 5);
  return person;
}

function updatePersonFromJson(person, personJson) {
  if (!personJson) return null;
  if (personJson.length == 0) return null;
  cache_clear(person_cacheName);
  if (person == null) person = new personClass();
  if (personJson[0].personId > 0) personJson = personJson[0];
  person.personId = personJson.personId;
  person.personFirstName = personJson.personFirstName;
  person.personLastName = personJson.personLastName;
  person.personPhone = personJson.personPhone;
  person.personEmail = personJson.personEmail;
  person.personMemo = personJson.personMemo;
  person.lastLoginTime = personJson.lastLoginTime;
  person.darkMode = personJson.darkMode;
  person.ownerAsiakasId = personJson.ownerAsiakasId;
  person.dontAsks.personId = personJson.personId;
  return person;
}
async function updatePersonSettings(person, token) {
  if (person == null) return null;
  cache_clear(person_cacheName);
  const settings = new personSettingsClass(person.personId, token);
  await settings.init(person.token);
  person.settings = settings;
  return settings;
}

export async function person_mergePerson(personIdToKeep, personIdToRemove, token) {
  try {
    cache_clear(person_cacheName);
    const url1 = import.meta.env.VITE_SERVER_URL + "/api/person/mergePersons";
    const requestOptions = {
      method: "POST",
      headers: {
        "x-access-token": `${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        personIdToKeep: personIdToKeep,
        personIdToDelete: personIdToRemove,
      }),
    };
    const response = await fetch(url1, requestOptions);
    const cResponse = await checkResponse(response, "personFunctions.js:person_mergePerson");
    if (!cResponse.success) return false;
    const json = cResponse.data;
    return json;
  } catch (err) {
    console.log(err);
  }
}

export async function person_delete(personId, token) {
  try {
    cache_clear(person_cacheName);
    const url1 = import.meta.env.VITE_SERVER_URL + "/api/person/delete/" + personId;
    const requestOptions = {
      method: "DELETE",
      headers: {
        "x-access-token": `${token}`,
        "Content-Type": "application/json",
      },
    };
    const response = await fetch(url1, requestOptions);
    return response.ok;
  } catch (err) {
    console.log(err);
  }
}

export async function fetchDefaultPumpputoimittajaAsiakasId(personId, token) {
  const a = await fetchPersonSetting(personId, 5, token);
  return a.intVar;
}
export async function fetchDefaultBetonitoimittajaAsiakasId(personId, token) {
  const a = await fetchPersonSetting(personId, 6, token);
  return a.intVar;
}
export async function fetchDefaultLattiatoimittajaAsiakasId(personId, token) {
  const a = await fetchPersonSetting(personId, 7, token);
  return a.intVar;
}
export async function fetchUserAsiakasListForPerson(personId, token) {
  const fullUrl = import.meta.env.VITE_SERVER_URL + "/api/person/getUserAsiakasList/" + personId;
  const c = cache_read(person_cacheName, fullUrl);
  if (c) return c;
  const requestOptions = {
    method: "GET",
    headers: {
      "x-access-token": `${token}`,
      "Content-Type": "application/json",
    },
  };
  const response = await fetch(fullUrl, requestOptions);
  const cResponse = await checkResponse(
    response,
    "personFunctions.js:fetchUserAsiakasListForPerson"
  );
  if (!cResponse.success) return [];
  const result = cResponse.data;
  cache_write(person_cacheName, fullUrl, result, 2880);
  return result;
}

export async function person_fetchForeignKeys(personId, ownerAsiakasId, token) {
  const fullUrl =
    import.meta.env.VITE_SERVER_URL +
    "/api/person/getForeignKeys/" +
    personId +
    "/" +
    ownerAsiakasId;
  const c = cache_read("person_foreignKeys", fullUrl);
  if (c) return c;
  const requestOptions = {
    method: "GET",
    headers: {
      "x-access-token": `${token}`,
      "Content-Type": "application/json",
    },
  };
  const response = await fetch(fullUrl, requestOptions);
  const cResponse = await checkResponse(response, "personFunctions.js:person_getForeignKeys");
  if (!cResponse.success) return [];
  const result = cResponse.data;
  cache_write(person_cacheName, fullUrl, result, 2880);
  return result;
}
export async function person_saveForeignKey(
  personId,
  personForeignKeyId,
  foreignKeyValue,
  foreignKeySourceId,
  foreignKeyText,
  ownerAsiakasId,
  isDisabled,
  token
) {
  const fullUrl =
    import.meta.env.VITE_SERVER_URL +
    "/api/person/saveForeignKey/" +
    personId +
    "/" +
    ownerAsiakasId;
  const requestOptions = {
    method: "POST",
    headers: {
      "x-access-token": `${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      personForeignKeyId: personForeignKeyId,
      foreignKey: foreignKeyValue,
      foreignKeySourceId: foreignKeySourceId,
      foreignKeyText: foreignKeyText,
      isDisabled: isDisabled,
    }),
  };
  const response = await fetch(fullUrl, requestOptions);
  // clear cache
  cache_clear("person_foreignKeys");
  return response.ok;
}
